import { Fragment, useEffect, useState } from "react";
import { rentalPokemon } from "../data/pokemon";
import { FactoryPokemon } from "./FactoryPokemon";
import { battleStyles, PokemonType, pokemonTypes } from "./FactoryTypes";
import { HomeButton } from "./HomeButton";

export const FactoryTracker = () => {
  const {
    pinned: localPinned,
    myPokemon: localMyPokemon,
    blacklisted: localBlacklisted,
    search: localSearch,
    typeSpecialty: localTypeSpecialty,
    battleStyle: localBattleStyle,
    hideUnpinned: localHideUnpinned,
  } = JSON.parse(localStorage.getItem("state") ?? "{}");

  const [pinned, setPinned] = useState<string[]>(localPinned ?? []);
  const [myPokemon, setMyPokemon] = useState<string[]>(localMyPokemon ?? []);
  const [blacklisted, setBlacklisted] = useState<string[]>(localBlacklisted ?? []);
  const [search, setSearch] = useState<string>(localSearch ?? "");
  const [typeSpecialty, setTypeSpecialty] = useState<PokemonType | undefined>(localTypeSpecialty);
  const [battleStyle, setBattleStyle] = useState<number | undefined>(localBattleStyle);
  const [hideUnpinned, setHideUnpinned] = useState(!!localHideUnpinned);

  useEffect(
    () =>
      localStorage.setItem(
        "state",
        JSON.stringify({ pinned, myPokemon, blacklisted, search, typeSpecialty, battleStyle, hideUnpinned }),
      ),
    [pinned, myPokemon, blacklisted, search, typeSpecialty, battleStyle, hideUnpinned],
  );

  return (
    <div className="factory-tracker">
      <HomeButton />
      <h1>Pokémon Emerald Factory Tracker</h1>

      <div className="options">
        <div className="reset-button">
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setPinned([]);
              setBlacklisted([]);
              setMyPokemon([]);
              setTypeSpecialty(undefined);
              setBattleStyle(undefined);
            }}
            className="info-move normal"
          >
            Reset Tracker
          </button>
        </div>

        <label>
          <span>Type Specialty: </span>
        </label>

        <div className="type-buttons">
          <button onClick={() => setTypeSpecialty(undefined)} className="info-move curse">
            No Type
          </button>
          {pokemonTypes.slice(0, 17).map((pkmnType) => (
            <button
              onClick={() => setTypeSpecialty(pkmnType)}
              key={pkmnType}
              className={`info-move ${pkmnType.toLowerCase()}`}
            >
              {pkmnType}
            </button>
          ))}
        </div>

        <label htmlFor="style">
          <span>Battle Style: </span>
        </label>
        <div className="type-buttons">
          {battleStyles.map((battleStyle, index) => (
            <button onClick={() => setBattleStyle(index)} key={battleStyle} className={`info-move normal`}>
              {battleStyle}
            </button>
          ))}
        </div>

        <label htmlFor="search">
          <span>Search Pokémon: </span>
          <input id="search" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button
            type="button"
            onClick={() => {
              setHideUnpinned(!hideUnpinned);
            }}
            className="info-move normal"
          >
            {hideUnpinned ? "Show" : "Hide"} Unpinned Pokémon
          </button>
        </label>
      </div>

      <div className="pokemon-list">
        {myPokemon.map((myPkmn) => {
          const foundPokemon = rentalPokemon.find((pokemon) => myPkmn === `${pokemon.species} ${pokemon.set}`);

          if (!foundPokemon) return <Fragment key={`my ${myPkmn}`} />;

          return (
            <FactoryPokemon
              pokemon={foundPokemon}
              key={`my ${myPkmn}`}
              myPokemon
              setPinned={(name: string) => setPinned(pinned.filter((pin) => pin !== name))}
              setMyPokemon={(name: string) => setMyPokemon(myPokemon.filter((pkmn) => pkmn !== name))}
              blacklisted={blacklisted.includes(`${foundPokemon.species} ${foundPokemon.set}`)}
              setBlacklisted={
                blacklisted.includes(`${foundPokemon.species} ${foundPokemon.set}`)
                  ? (name: string) => setBlacklisted(blacklisted.filter((blName) => blName !== name))
                  : (name: string) => setBlacklisted([...blacklisted, name])
              }
            />
          );
        })}
      </div>

      <div className="opponent-info">
        <p>Opponent Information</p>
        <div>
          <p className={`info-move ${typeSpecialty?.toLowerCase()}`}>{typeSpecialty ? typeSpecialty : "No Type"} </p>
          <p className="info-style">
            {battleStyle !== undefined ? `${battleStyles[battleStyle]} (${battleStyle})` : "Unselected"}
          </p>
        </div>
      </div>

      <div className="pokemon-list pinned-list">
        {pinned.map((pin) => {
          const pinnedPokemon = rentalPokemon.find((pokemon) => pin === `${pokemon.species} ${pokemon.set}`);

          if (!pinnedPokemon) return <Fragment key={`pin ${pin}`} />;

          return (
            <FactoryPokemon
              pokemon={pinnedPokemon}
              key={`pin ${pin}`}
              pinned
              setPinned={(name: string) => {
                setPinned(pinned.filter((pin) => pin !== name));
              }}
              setMyPokemon={(name: string) => {
                setMyPokemon([...myPokemon, name]);
                setPinned(pinned.filter((pin) => pin !== name));
              }}
              blacklisted={blacklisted.includes(`${pinnedPokemon.species} ${pinnedPokemon.set}`)}
              setBlacklisted={
                blacklisted.includes(`${pinnedPokemon.species} ${pinnedPokemon.set}`)
                  ? (name: string) => setBlacklisted(blacklisted.filter((blName) => blName !== name))
                  : (name: string) => setBlacklisted([...blacklisted, name])
              }
            />
          );
        })}
      </div>

      <div className="pokemon-list">
        {!hideUnpinned &&
          rentalPokemon.map((pokemon) => {
            if ([...pinned, ...myPokemon].includes(`${pokemon.species} ${pokemon.set}`))
              return <Fragment key={`${pokemon.species} ${pokemon.set}`} />;

            if (search.length === 0 || `${pokemon.species} ${pokemon.set}`.toLowerCase().includes(search.toLowerCase()))
              return (
                <FactoryPokemon
                  pokemon={pokemon}
                  key={`${pokemon.species} ${pokemon.set}`}
                  setPinned={(name: string) => setPinned([...pinned, name])}
                  setMyPokemon={(name: string) => setMyPokemon([...myPokemon, name])}
                  blacklisted={blacklisted.includes(`${pokemon.species} ${pokemon.set}`)}
                  setBlacklisted={
                    blacklisted.includes(`${pokemon.species} ${pokemon.set}`)
                      ? (name: string) => setBlacklisted(blacklisted.filter((blName) => blName !== name))
                      : (name: string) => setBlacklisted([...blacklisted, name])
                  }
                />
              );

            return <Fragment key={`${pokemon.species} ${pokemon.set}`} />;
          })}
      </div>
      <div className="spacer"></div>
    </div>
  );
};
